import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight, ShieldCheck, Clock, BadgeCheck } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1644246905181-c3753e9a82bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxkcml2ZXIlMjByZWZ1ZWxpbmclMjBjYXIlMjBhdCUyMHBldHJvbCUyMHN0YXRpb258ZW58MHx8fHwxNzg2MzQ3NjU0fDA&ixlib=rb-4.1.0&q=85";

const word = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

const trust = [
  { icon: Clock, label: "~40 min arrival" },
  { icon: ShieldCheck, label: "Fully insured" },
  { icon: BadgeCheck, label: "No fix, no fee" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#F5F3EF] pt-32 pb-20 md:pt-40 md:pb-28"
    >
      <div className="absolute inset-0 halftone opacity-60" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 text-sm font-medium text-[#171717]/70"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 rounded-full bg-[#1F7A6B]" /> Wrong fuel service · Sydney wide · 24/7
          </motion.span>

          <h1 className="font-heading leading-[1.02] text-[#171717]" data-testid="hero-heading">
            {["Don't stress.", "Help is on"].map((t, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={word} initial="hidden" animate="show" className="inline-block text-4xl sm:text-5xl lg:text-6xl">
                  {t}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden">
              <motion.span custom={2} variants={word} initial="hidden" animate="show" className="inline-block text-4xl sm:text-5xl lg:text-6xl text-[#1F7A6B]">
                the way.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-[#171717]/70"
          >
            Put petrol in a diesel — or diesel in a petrol? It happens. Our mobile team comes to you
            anywhere in Sydney, drains and flushes your fuel system, and gets you safely back on the road.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-[#1F7A6B] px-7 py-4 text-lg font-semibold text-white hover:bg-[#185f54] transition-colors"
            >
              <Phone strokeWidth={2.5} className="h-5 w-5" /> Call {PHONE_DISPLAY}
            </a>
            <a
              href="#request"
              data-testid="hero-request-button"
              className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-7 py-4 text-lg font-semibold text-[#171717] hover:border-black/30 transition-colors"
            >
              Request a rescue <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75 }}
            className="mt-8 flex flex-wrap gap-x-7 gap-y-3"
          >
            {trust.map((t) => (
              <span key={t.label} className="flex items-center gap-2 text-sm font-medium text-[#171717]/65">
                <t.icon strokeWidth={2} className="h-4 w-4 text-[#1F7A6B]" /> {t.label}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-full"
        >
          <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-2xl border border-black/10 bg-white shadow-soft">
            <img src={HERO_IMG} alt="Driver refuelling a car at a petrol station" className="h-full w-full object-cover" data-testid="hero-image" />
          </motion.div>
          <div
            className="absolute -bottom-5 left-5 rounded-xl border border-black/10 bg-white px-5 py-4 shadow-soft-sm"
            data-testid="hero-no-fee-badge"
          >
            <div className="font-heading text-2xl text-[#171717]">~40 min</div>
            <div className="text-xs font-medium uppercase tracking-wide text-[#171717]/55">Average arrival</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
